import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Loader2, ArrowRight, Calendar, LogOut, BarChart3, Target, Clock, Shield, TrendingUp, Users, ChevronDown, ChevronUp, Activity, Download, Presentation, AlertTriangle, DollarSign, Rocket, Flame } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from "recharts";
import { motion } from "framer-motion";
import { trackReportDownloaded } from "../lib/analytics";
import { usePageSEO } from "../lib/seo";
import ArchitectureDiagram from "../components/ArchitectureDiagram";

export default function ResultsDashboard() {
  const { auditId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedOpp, setExpandedOpp] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDeck, setDownloadingDeck] = useState(false);
  usePageSEO({ title: "Results", noIndex: true });

  useEffect(() => {
    const load = async () => {
      try {
        const { data: auditData } = await api.get(`/audit/${auditId}`);
        setAudit(auditData);
        if (auditData.company_info?.industry) {
          try {
            const subParam = auditData.company_info.sub_industry ? `?sub_industry=${auditData.company_info.sub_industry}` : "";
            const { data: b } = await api.get(`/benchmark/${auditData.company_info.industry}${subParam}`);
            setBenchmark(b);
          } catch (e) { console.error("Benchmark load failed:", e); }
        }
        if (auditData.payment_status === "paid") {
          if (auditData.report) { setReport(auditData.report); }
          else { setGenerating(true); const { data: r } = await api.post(`/report/generate/${auditId}`); setReport(r.report); setGenerating(false); }
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId]);

  if (loading || generating) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="text-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC] mx-auto mb-4" /><p className="text-sm text-slate-500">{generating ? "Generating your AI Growth Blueprint..." : "Loading..."}</p></div></div>;
  }
  if (!audit || audit.payment_status !== "paid") {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="text-center"><p className="text-lg font-['Outfit'] text-[#0F172A] mb-4">Report not yet unlocked</p><Button onClick={() => navigate(`/score-preview/${auditId}`)} className="bg-[#0033CC] text-white rounded-none font-['Outfit']">Unlock Report</Button></div></div>;
  }

  const scores = audit.scores || {};
  const sections = scores.sections || {};
  const snap = report?.executive_snapshot || {};
  const fin = report?.financial_impact || {};
  const diag = report?.diagnosis || {};
  const opps = report?.opportunity_stack?.opportunities || report?.opportunities || [];
  const strat = report?.strategy || {};
  const roadmap = report?.roadmap || {};
  const outcomes = report?.outcomes || {};
  const risk = report?.risk_of_inaction || {};
  const impl = report?.implementation_path || {};
  const chartData = Object.entries(sections).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), score: v.percentage || 0 }));
  const radarData = chartData.map(d => ({ subject: d.name, score: d.score, fullMark: 100 }));
  const tierColors = { "Reactive": "#EF4444", "Emerging": "#F59E0B", "Operationalizing": "#818CF8", "Systemized": "#10B981", "AI-Native": "#0033CC" };

  const handleLogout = async () => { await logout(); navigate("/"); };
  const downloadFile = async (type) => {
    const setter = type === "pdf" ? setDownloading : setDownloadingDeck;
    setter(true);
    try {
      const ep = type === "pdf" ? `/report/${auditId}/pdf` : `/report/${auditId}/deck`;
      const res = await api.get(ep, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `Stansbury_AI_${type === "pdf" ? "Blueprint" : "Exec_Deck"}_${audit?.company_info?.company_name?.replace(/\s/g, "_") || "Report"}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      trackReportDownloaded(type);
    } catch (err) { console.error(err); }
    finally { setter(false); }
  };

  const TT = ({ value, children }) => <TabsTrigger value={value} className="rounded-none font-['Outfit'] text-xs sm:text-sm data-[state=active]:bg-[#0033CC] data-[state=active]:text-white">{children}</TabsTrigger>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="results-dashboard">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 sm:h-12 flex-shrink-0" />
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sm text-slate-500 font-['Outfit']">Dashboard</Button>
            <Button onClick={() => downloadFile("pdf")} disabled={downloading} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-3 h-9 text-xs sm:text-sm font-['Outfit']" data-testid="download-pdf-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" />PDF</>}
            </Button>
            <Button onClick={() => downloadFile("deck")} disabled={downloadingDeck} variant="outline" className="border-[#0033CC] text-[#0033CC] rounded-none px-3 h-9 text-xs sm:text-sm font-['Outfit']" data-testid="download-deck-btn">
              {downloadingDeck ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Presentation className="w-3.5 h-3.5 mr-1" />Deck</>}
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-sm text-slate-500"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Headline Finding */}
        {snap.headline_finding && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0F172A] p-6 mb-8">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-2">Key Finding</p>
            <p className="text-base sm:text-lg font-['Outfit'] text-white font-medium leading-relaxed">{snap.headline_finding}</p>
          </motion.div>
        )}

        {/* Score + Financial Impact Row */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white border border-slate-200 p-5">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">Score</p>
            <div className="text-4xl font-['Outfit'] font-light text-[#0033CC]">{scores.overall || 0}</div>
            <div className="flex items-center gap-1.5 mt-2"><div className="w-2 h-2" style={{ backgroundColor: tierColors[scores.tier] || "#818CF8" }} /><span className="text-xs font-['Outfit'] font-medium">{scores.tier}</span></div>
          </div>
          <div className="bg-white border border-slate-200 p-5">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">12-Month Opportunity</p>
            <div className="text-xl font-['Outfit'] font-medium text-emerald-600">{snap.projected_revenue_opportunity_12m || ""}</div>
          </div>
          <div className="bg-white border border-slate-200 p-5">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">Annual Upside</p>
            <div className="text-xl font-['Outfit'] font-medium text-emerald-600">${fin.impact?.annual_upside || ""}</div>
          </div>
          <div className="bg-white border border-slate-200 p-5">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">Payback</p>
            <div className="text-xl font-['Outfit'] font-medium text-[#0F172A]">{fin.impact?.payback_period_days || ""} days</div>
          </div>
          <div className="bg-white border border-slate-200 p-5">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-2">Revenue Leak</p>
            <div className="text-xl font-['Outfit'] font-medium text-red-600">{scores.revenue_leak_severity || ""}</div>
          </div>
        </motion.div>

        {/* Charts */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-6"><h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Category Scores</h3><ResponsiveContainer width="100%" height={220}><BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} /><YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#64748B" }} /><Tooltip /><Bar dataKey="score" fill="#0033CC" /></BarChart></ResponsiveContainer></div>
          <div className="bg-white border border-slate-200 p-6"><h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Growth Radar</h3><ResponsiveContainer width="100%" height={220}><RadarChart data={radarData}><PolarGrid stroke="#E2E8F0" /><PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "#64748B" }} /><PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} /><Radar dataKey="score" stroke="#0033CC" fill="#0033CC" fillOpacity={0.15} strokeWidth={2} /></RadarChart></ResponsiveContainer></div>
        </motion.div>

        {/* Report Tabs */}
        {report && (
          <Tabs defaultValue="snapshot" className="mb-10" data-testid="report-tabs">
            <TabsList className="bg-white border border-slate-200 rounded-none p-1 mb-6 flex-wrap h-auto gap-0.5">
              <TT value="snapshot">Executive</TT>
              {report?.revenue_leak_analysis && <TT value="leaks">Revenue Leaks</TT>}
              <TT value="financial">Financial Model</TT>
              <TT value="diagnosis">Diagnosis</TT>
              {report?.opportunity_matrix && <TT value="opp-matrix">Opportunity Matrix</TT>}
              {!report?.opportunity_matrix && <TT value="opportunities">Opportunities</TT>}
              {report?.department_transformation && <TT value="dept-transform">Departments</TT>}
              <TT value="strategy">Strategy</TT>
              <TT value="roadmap">Roadmap</TT>
              {report?.roi_model && <TT value="roi">ROI Model</TT>}
              <TT value="outcomes">Outcomes</TT>
              <TT value="risk">Risk</TT>
              <TT value="next">Next Steps</TT>
              {report?.agent_architecture && <TT value="architecture">Architecture</TT>}
              {benchmark && <TT value="benchmark">Benchmark</TT>}
              {report?.system_designs && <TT value="systems">Systems</TT>}
              {report?.tool_stack && <TT value="toolstack">Tool Stack</TT>}
            </TabsList>

            {/* EXECUTIVE SNAPSHOT */}
            <TabsContent value="snapshot"><div className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3">Company Overview</h3>
                <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{snap.company_overview || report.executive_summary || ""}</p>
              </div>
              {snap.top_constraints?.length > 0 && (
                <div className="bg-white border border-slate-200 p-6">
                  <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3">Top Constraints</h3>
                  {snap.top_constraints.map((c, i) => (
                    <div key={`c-${i}`} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                      <span className="w-6 h-6 bg-red-50 text-red-700 flex items-center justify-center text-xs font-['JetBrains_Mono'] flex-shrink-0">{i+1}</span>
                      <p className="text-sm text-slate-600 font-['IBM_Plex_Sans']">{c}</p>
                    </div>
                  ))}
                </div>
              )}
            </div></TabsContent>

            {/* REVENUE LEAK ANALYSIS */}
            {report?.revenue_leak_analysis && (
              <TabsContent value="leaks"><div className="space-y-4" data-testid="revenue-leaks-tab">
                <div className="bg-white border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-red-600 mb-1">Total Estimated Revenue Leakage</h4>
                      <div className="text-3xl font-['Outfit'] font-light text-red-600">{report.revenue_leak_analysis.total_estimated_leakage}</div>
                    </div>
                    {report.revenue_leak_analysis.summary && <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] max-w-md leading-relaxed">{report.revenue_leak_analysis.summary}</p>}
                  </div>
                </div>
                {(report.revenue_leak_analysis.leak_categories || []).map((leak, i) => (
                  <div key={`leak-${i}`} className="bg-white border border-slate-200 p-5" data-testid={`leak-cat-${i}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className={`text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 mr-2 ${leak.severity === "High" ? "bg-red-50 text-red-700" : leak.severity === "Medium" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{leak.severity}</span>
                        <span className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{leak.category}</span>
                      </div>
                      <span className="text-sm font-['JetBrains_Mono'] text-red-600">{leak.estimated_annual_loss}</span>
                    </div>
                    <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-2">{leak.root_cause}</p>
                    <div className="flex gap-4 text-[10px] font-['JetBrains_Mono'] text-slate-400">
                      <span>Dept: {leak.department}</span>
                      <span>Fix: {leak.fix_complexity}</span>
                      <span>Timeline: {leak.fix_timeline}</span>
                    </div>
                  </div>
                ))}
                {report.revenue_leak_analysis.quick_wins && (
                  <div className="bg-emerald-50 border border-emerald-200 p-5">
                    <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-emerald-700 mb-3">Quick Wins (30 Days)</h4>
                    {report.revenue_leak_analysis.quick_wins.map((win, i) => (
                      <p key={`qw-${i}`} className="text-sm text-emerald-800 font-['IBM_Plex_Sans'] py-1">+ {win}</p>
                    ))}
                  </div>
                )}
              </div></TabsContent>
            )}

            {/* FINANCIAL IMPACT */}
            <TabsContent value="financial"><div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 p-6">
                  <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-3">Current State</h4>
                  {Object.entries(fin.current_state || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1.5 border-b border-slate-50 text-sm"><span className="text-slate-500 capitalize">{k.replace(/estimated_|_/g, " ").trim()}</span><span className="font-['JetBrains_Mono'] font-medium">{v}</span></div>
                  ))}
                </div>
                <div className="bg-white border border-slate-200 border-l-2 border-l-emerald-500 p-6">
                  <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-emerald-600 mb-3">Projected With AI</h4>
                  {Object.entries(fin.projected_state || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1.5 border-b border-slate-50 text-sm"><span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</span><span className="font-['JetBrains_Mono'] font-medium text-emerald-600">{v}</span></div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[{l:"Monthly Upside",v:fin.impact?.monthly_upside,p:"$"},{l:"Annual Upside",v:fin.impact?.annual_upside,p:"$",c:"text-emerald-600"},{l:"Payback Period",v:fin.impact?.payback_period_days,s:" days"}].map(item => (
                  <div key={item.l} className="bg-white border border-slate-200 p-5 text-center">
                    <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-2">{item.l}</p>
                    <div className={`text-2xl font-['Outfit'] font-medium ${item.c || "text-[#0F172A]"}`}>{item.p || ""}{item.v || ""}{item.s || ""}</div>
                  </div>
                ))}
              </div>
              {fin.methodology_note && <p className="text-xs text-slate-400 italic font-['IBM_Plex_Sans']">{fin.methodology_note}</p>}
            </div></TabsContent>

            {/* DIAGNOSIS */}
            <TabsContent value="diagnosis"><div className="space-y-4">
              {typeof diag === "string" ? <div className="bg-white border border-slate-200 p-6"><p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed whitespace-pre-line">{diag}</p></div> :
                ["acquire","convert","operate","expand","defend"].map(cat => {
                  const cd = diag[cat]; if (!cd) return null;
                  return (<div key={cat} className="bg-white border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-3"><h4 className="text-base font-['Outfit'] font-medium text-[#0F172A] capitalize">{cat}</h4><span className="text-sm font-['JetBrains_Mono'] text-[#0033CC]">{cd.score}%</span></div>
                    {(cd.breakdowns || []).map((b, j) => <p key={`d-${j}`} className="text-sm text-slate-600 font-['IBM_Plex_Sans'] py-1 border-b border-slate-50 last:border-0">- {b}</p>)}
                    {(cd.estimated_monthly_leakage || cd.estimated_hours_wasted_weekly || cd.estimated_ltv_gap || cd.competitive_vulnerability) && <p className="text-xs font-['JetBrains_Mono'] text-red-600 mt-2">{cd.estimated_monthly_leakage || cd.estimated_hours_wasted_weekly || cd.estimated_ltv_gap || cd.competitive_vulnerability}</p>}
                  </div>);
                })
              }
            </div></TabsContent>

            {/* OPPORTUNITIES */}
            <TabsContent value="opportunities"><div className="space-y-3">
              {opps.map((o, i) => { const isObj = typeof o === "object"; return (
                <div key={`o-${i}`} className="bg-white border border-slate-200 p-5 cursor-pointer hover:border-slate-300" onClick={() => setExpandedOpp(expandedOpp === i ? null : i)}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3"><span className="w-6 h-6 bg-[#0033CC] text-white flex items-center justify-center text-xs font-['JetBrains_Mono'] flex-shrink-0">{i+1}</span><h4 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{isObj ? (o.name || o.title) : o}</h4></div>
                    <div className="flex items-center gap-2 flex-shrink-0">{isObj && o.confidence_score && <span className="text-xs font-['JetBrains_Mono'] text-slate-400">{o.confidence_score}/10</span>}{isObj && o.difficulty && <span className={`text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 ${o.difficulty === "Low" ? "bg-green-50 text-green-700" : o.difficulty === "High" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{o.difficulty}</span>}{expandedOpp === i ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}</div>
                  </div>
                  {expandedOpp === i && isObj && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 pt-3 border-t border-slate-100 ml-9">
                    <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-3">{o.what_changes || o.description}</p>
                    <div className="flex gap-4 text-xs font-['JetBrains_Mono']">{o.revenue_impact_annual && <span className="text-emerald-600">{o.revenue_impact_annual}</span>}{o.time_to_cash_days && <span className="text-slate-400">{o.time_to_cash_days} days to results</span>}</div>
                  </motion.div>}
                </div>
              ); })}
            </div></TabsContent>

            {/* OPPORTUNITY MATRIX (V2) */}
            {report?.opportunity_matrix && (
              <TabsContent value="opp-matrix"><div className="space-y-3" data-testid="opp-matrix-tab">
                {(report.opportunity_matrix.opportunities || []).map((o, i) => (
                  <div key={`om-${i}`} className="bg-white border border-slate-200 p-5" data-testid={`opp-item-${i}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-3">
                        <span className={`text-[10px] font-['JetBrains_Mono'] px-2 py-1 ${o.priority === "P1" ? "bg-[#0033CC] text-white" : o.priority === "P2" ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-500"}`}>{o.priority}</span>
                        <div>
                          <h4 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{o.initiative}</h4>
                          <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mt-0.5">{o.department} &middot; {o.implementation_type}</p>
                        </div>
                      </div>
                      <span className="text-sm font-['JetBrains_Mono'] text-emerald-600">{o.revenue_impact}</span>
                    </div>
                    <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-2 ml-12">{o.description}</p>
                    <div className="flex gap-4 text-[10px] font-['JetBrains_Mono'] text-slate-400 ml-12">
                      <span>Difficulty: {o.difficulty}</span>
                      <span>Time to value: {o.time_to_value} days</span>
                      {o.staffing_impact && <span>Staffing: {o.staffing_impact}</span>}
                    </div>
                  </div>
                ))}
              </div></TabsContent>
            )}

            {/* DEPARTMENT TRANSFORMATION (V2) */}
            {report?.department_transformation && (
              <TabsContent value="dept-transform"><div className="space-y-4" data-testid="dept-transform-tab">
                {(report.department_transformation.departments || []).map((dept, i) => (
                  <div key={`dept-${i}`} className="bg-white border border-slate-200" data-testid={`dept-${i}`}>
                    <div className="p-5 border-b border-slate-100">
                      <h4 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-1">{dept.name}</h4>
                    </div>
                    <div className="grid grid-cols-2 divide-x divide-slate-100">
                      <div className="p-5">
                        <h5 className="text-xs font-['JetBrains_Mono'] uppercase text-red-500 mb-2">Current State</h5>
                        <p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{dept.current_state}</p>
                      </div>
                      <div className="p-5 bg-emerald-50/30">
                        <h5 className="text-xs font-['JetBrains_Mono'] uppercase text-emerald-600 mb-2">Future State</h5>
                        <p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{dept.future_state}</p>
                      </div>
                    </div>
                    {dept.operational_impact && (
                      <div className="p-4 bg-slate-50 border-t border-slate-100 grid grid-cols-4 gap-3">
                        {Object.entries(dept.operational_impact).map(([k, v]) => (
                          <div key={k}><span className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 block mb-0.5">{k.replace(/_/g, " ")}</span><span className="text-xs font-['IBM_Plex_Sans'] text-[#0F172A] font-medium">{String(v)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div></TabsContent>
            )}

            {/* STRATEGY */}
            <TabsContent value="strategy"><div className="space-y-4">
              {["acquire","convert","operate","expand","defend"].map((cat, i) => {
                const sd = strat[cat]; if (!sd) return null;
                const isObj = typeof sd === "object";
                const colors = ["#059669","#0033CC","#7C3AED","#F59E0B","#DC2626"];
                return (<div key={cat} className="bg-white border border-slate-200 border-l-2 p-6" style={{borderLeftColor: colors[i]}}>
                  <h4 className="text-base font-['Outfit'] font-medium text-[#0F172A] capitalize mb-2">{cat}</h4>
                  <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-3">{isObj ? sd.strategy : sd}</p>
                  {isObj && sd.agentic_workflows?.length > 0 && <div className="mb-2">{sd.agentic_workflows.map((w, j) => <p key={`w-${j}`} className="text-xs text-slate-500 py-0.5">+ {w}</p>)}</div>}
                  {isObj && sd.expected_outcome && <p className="text-xs font-['JetBrains_Mono'] text-emerald-600">{sd.expected_outcome}</p>}
                </div>);
              })}
            </div></TabsContent>

            {/* ROADMAP */}
            <TabsContent value="roadmap"><div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[{key:"phase_1",label:"0-30 Days",color:"#059669"},{key:"phase_2",label:"30-90 Days",color:"#0033CC"},{key:"phase_3",label:"90+ Days",color:"#7C3AED"}].map(({key,label,color}) => {
                const p = roadmap[key] || {};
                const items = key === "phase_1" ? [...(p.week_1||[]),...(p.week_2||[]),...(p.week_3||[]),...(p.week_4||[])] : (p.actions || []);
                // Fallback to legacy
                const legacy = report.roadmap_legacy || {};
                const fallbackItems = key === "phase_1" ? legacy.days_0_30 : key === "phase_2" ? legacy.days_30_90 : legacy.days_90_plus;
                const finalItems = items.length > 0 ? items : (fallbackItems || []);
                return (<div key={key} className="bg-white border border-slate-200 border-t-2 p-5" style={{borderTopColor:color}}>
                  <h4 className="text-sm font-['Outfit'] font-medium mb-1">{p.title || label}</h4>
                  <div className="space-y-1 mb-3">{finalItems.map((a, j) => <p key={`r-${j}`} className="text-xs text-slate-600 font-['IBM_Plex_Sans']" style={{color: "#475569"}}>+ {a}</p>)}</div>
                  {p.milestone && <div className="bg-slate-50 p-2 border-l-2" style={{borderColor:color}}><p className="text-[10px] font-['JetBrains_Mono']" style={{color}}>{p.milestone}</p></div>}
                </div>);
              })}
            </div></TabsContent>

            {/* OUTCOMES */}
            <TabsContent value="outcomes"><div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(report.expected_outcomes || {}).map(([k, v]) => (
                  <div key={k} className="bg-white border border-slate-100 p-4"><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">{k.replace(/_/g, " ")}</p><p className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A] font-medium">{v}</p></div>
                ))}
              </div>
              {outcomes.day_in_the_life && (
                <div className="bg-white border border-slate-200 p-6">
                  <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">A Day in the Life -- With Your AI Growth Engine</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[{t:"Morning",c:"#059669",v:outcomes.day_in_the_life.morning},{t:"Midday",c:"#0033CC",v:outcomes.day_in_the_life.midday},{t:"Afternoon",c:"#7C3AED",v:outcomes.day_in_the_life.afternoon}].map(s => (
                      <div key={s.t} className="border-l-2 pl-4" style={{borderColor:s.c}}><p className="text-xs font-['JetBrains_Mono'] mb-1" style={{color:s.c}}>{s.t}</p><p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{s.v}</p></div>
                    ))}
                  </div>
                  {outcomes.day_in_the_life.daily_numbers && <p className="text-xs font-['JetBrains_Mono'] text-emerald-600 bg-emerald-50 p-3 mt-4">{outcomes.day_in_the_life.daily_numbers}</p>}
                </div>
              )}
            </div></TabsContent>

            {/* ROI MODEL (V2) */}
            {report?.roi_model && (
              <TabsContent value="roi"><div className="space-y-4" data-testid="roi-model-tab">
                <div className="bg-white border border-slate-200 p-6">
                  <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-[#0033CC] mb-3">Investment Summary</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(report.roi_model.investment_summary || {}).map(([k, v]) => (
                      <div key={k} className="border border-slate-200 p-4 text-center">
                        <span className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 block mb-1">{k.replace(/_/g, " ")}</span>
                        <span className="text-lg font-['Outfit'] font-medium text-[#0F172A]">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 p-6">
                  <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-emerald-600 mb-3">Projected Returns</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(report.roi_model.projected_returns || {}).map(([k, v]) => (
                      <div key={k} className="border border-emerald-100 bg-emerald-50/30 p-3">
                        <span className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 block mb-1">{k.replace(/_/g, " ")}</span>
                        <span className="text-sm font-['IBM_Plex_Sans'] font-medium text-[#0F172A]">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#0F172A] p-6">
                  <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-[#818CF8] mb-3">Transformation Economics</h4>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {Object.entries(report.roi_model.economics || {}).map(([k, v]) => (
                      <div key={k} className="text-center">
                        <span className="text-xs text-slate-400 block mb-1">{k.replace(/_/g, " ")}</span>
                        <span className="text-xl font-['Outfit'] font-light text-white">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                  {report.roi_model.executive_summary && <p className="text-sm text-slate-300 font-['IBM_Plex_Sans'] leading-relaxed border-t border-white/10 pt-4">{report.roi_model.executive_summary}</p>}
                </div>
              </div></TabsContent>
            )}

            {/* RISK OF INACTION */}
            <TabsContent value="risk"><div className="space-y-4">
              <div className="bg-[#0F172A] p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center"><p className="text-xs font-['JetBrains_Mono'] uppercase text-red-400 mb-1">Lost Revenue (12 Months)</p><div className="text-2xl font-['Outfit'] font-medium text-red-400">{risk.lost_revenue_12m}</div></div>
                  <div className="text-center"><p className="text-xs font-['JetBrains_Mono'] uppercase text-amber-400 mb-1">Operational Cost</p><div className="text-lg font-['Outfit'] font-medium text-amber-400">{risk.operational_cost}</div></div>
                </div>
                <p className="text-sm text-slate-300 font-['IBM_Plex_Sans'] leading-relaxed mb-3">{risk.competitive_gap}</p>
                <p className="text-sm text-slate-400 font-['IBM_Plex_Sans'] mb-3">{risk.market_timing}</p>
                <div className="border-l-3 border-red-500 pl-4"><p className="text-base font-['Outfit'] font-medium text-white">{risk.bottom_line}</p></div>
              </div>
            </div></TabsContent>

            {/* NEXT STEPS */}
            <TabsContent value="next"><div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {impl.option_3 && (
                  <div className="bg-white border-2 border-[#0033CC] p-6">
                    <p className="text-xs font-['JetBrains_Mono'] uppercase text-[#0033CC] mb-2">Recommended</p>
                    <h4 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3">{impl.option_3.name}</h4>
                    <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">{impl.option_3.description}</p>
                    <p className="text-xs font-['JetBrains_Mono'] text-emerald-600 mb-4">Best for: {impl.option_3.best_for}</p>
                    {impl.option_3.outcome && <p className="text-xs font-['IBM_Plex_Sans'] text-slate-500 mb-4">{impl.option_3.outcome}</p>}
                    <Button onClick={() => navigate(`/booking/${auditId}`)} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-6 font-['Outfit'] text-sm"><Calendar className="w-4 h-4 mr-2" />Book a Call</Button>
                  </div>
                )}
                {impl.option_1 && (
                  <div className="bg-white border border-slate-200 p-6">
                    <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-2">{impl.option_1.tag || "Alternative"}</p>
                    <h4 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3">{impl.option_1.name}</h4>
                    <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">{impl.option_1.description}</p>
                    <p className="text-xs font-['JetBrains_Mono'] text-slate-500 mb-2">Best for: {impl.option_1.best_for}</p>
                    {impl.option_1.risk && <p className="text-xs text-red-600 font-['IBM_Plex_Sans']">{impl.option_1.risk}</p>}
                  </div>
                )}
              </div>
              {impl.recommendation && <div className="bg-slate-50 border border-slate-200 p-4"><p className="text-xs font-['JetBrains_Mono'] uppercase text-[#0033CC] mb-1">Our Recommendation</p><p className="text-sm text-slate-600 font-['IBM_Plex_Sans']">{impl.recommendation}</p></div>}
            </div></TabsContent>

            {/* BENCHMARK */}
            {benchmark && (
              <TabsContent value="benchmark"><div className="space-y-4">
                {/* Percentile & summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white border border-slate-200 p-5" data-testid="benchmark-overall-avg">
                    <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Industry Average</p>
                    <p className="text-2xl font-['Outfit'] font-light text-[#0033CC]">{benchmark.overall_average}/100</p>
                    <p className="text-xs text-slate-500 mt-1">{benchmark.industry_label || benchmark.industry?.replace(/_/g, " ")}</p>
                  </div>
                  {benchmark.percentile !== null && benchmark.percentile !== undefined && (
                    <div className="bg-white border border-slate-200 p-5" data-testid="benchmark-percentile">
                      <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Your Percentile</p>
                      <p className="text-2xl font-['Outfit'] font-light text-[#0033CC]">Top {100 - benchmark.percentile}%</p>
                      <p className="text-xs text-slate-500 mt-1">vs {benchmark.industry_label || benchmark.industry?.replace(/_/g, " ")} peers</p>
                    </div>
                  )}
                  <div className="bg-white border border-slate-200 p-5" data-testid="benchmark-source">
                    <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Data Source</p>
                    <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] mt-1">
                      {benchmark.source === "dynamic" ? "Real Audit Data" : benchmark.source === "blended" ? "Blended (Real + Baseline)" : "Industry Baseline"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{benchmark.sample_size} {benchmark.source === "industry_baseline" ? "baseline" : "audit"} sample{benchmark.sample_size !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                {/* Radar Chart */}
                <div className="bg-white border border-slate-200 p-6">
                  <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Your Scores vs {benchmark.industry_label || benchmark.industry?.replace(/_/g, " ")} Average</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <RadarChart data={Object.keys(benchmark.benchmark || {}).map(k => ({ subject: k.charAt(0).toUpperCase() + k.slice(1), yours: sections[k]?.percentage || 0, industry: benchmark.benchmark[k] || 0, fullMark: 100 }))}>
                      <PolarGrid stroke="#E2E8F0" /><PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: "#64748B" }} /><PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} />
                      <Radar name="Your Score" dataKey="yours" stroke="#0033CC" fill="#0033CC" fillOpacity={0.15} strokeWidth={2} /><Radar name="Industry Avg" dataKey="industry" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.08} strokeDasharray="4 4" /><Legend wrapperStyle={{ fontSize: 12 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                {/* Category comparison table */}
                <div className="bg-white border border-slate-200 p-6">
                  <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Category Comparison</h3>
                  <div className="space-y-3">
                    {Object.keys(benchmark.benchmark || {}).map(k => {
                      const yours = sections[k]?.percentage || 0;
                      const avg = benchmark.benchmark[k] || 0;
                      const diff = yours - avg;
                      return (
                        <div key={k} className="flex items-center gap-4" data-testid={`benchmark-cat-${k}`}>
                          <span className="text-xs font-['Outfit'] font-medium text-[#0F172A] w-20">{k.charAt(0).toUpperCase() + k.slice(1)}</span>
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden relative">
                            <div className="absolute h-full bg-[#0033CC] rounded-full" style={{ width: `${yours}%` }} />
                            <div className="absolute h-full w-0.5 bg-slate-400" style={{ left: `${avg}%` }} />
                          </div>
                          <span className="text-xs font-['JetBrains_Mono'] w-10 text-right">{yours}%</span>
                          <span className={`text-xs font-['JetBrains_Mono'] w-12 text-right ${diff >= 0 ? "text-emerald-600" : "text-red-500"}`}>{diff >= 0 ? "+" : ""}{diff}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div></TabsContent>
            )}

            {/* ARCHITECTURE DIAGRAM */}
            {report?.agent_architecture && (
              <TabsContent value="architecture"><div data-testid="architecture-tab">
                <div className="mb-4">
                  <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-[#0033CC] mb-1">System Architecture</h4>
                  <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">Interactive diagram of your AI growth infrastructure. Drag nodes to rearrange. Scroll to zoom.</p>
                </div>
                <ArchitectureDiagram agentArchitecture={report.agent_architecture} systemDesigns={report.system_designs} />
                {report.agent_architecture.data_flow && (
                  <div className="mt-4 bg-slate-50 border border-slate-200 p-4">
                    <h5 className="text-xs font-['JetBrains_Mono'] uppercase text-[#0033CC] mb-2">Data Flow</h5>
                    <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{report.agent_architecture.data_flow}</p>
                  </div>
                )}
              </div></TabsContent>
            )}

            {/* SYSTEM DESIGNS (White Glove) */}
            {report?.system_designs?.systems && (
              <TabsContent value="systems"><div className="space-y-4">
                {report.system_designs.systems.map((sys, i) => (
                  <div key={`sys-${i}`} className="bg-white border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-3"><span className="w-7 h-7 bg-[#0033CC] text-white flex items-center justify-center text-xs font-['JetBrains_Mono']">{i+1}</span><div><h4 className="text-base font-['Outfit'] font-medium text-[#0F172A]">{sys.name}</h4><p className="text-xs text-slate-500">{sys.purpose}</p></div></div>
                    <div className="flex gap-2 mb-3"><span className="text-xs font-['JetBrains_Mono'] bg-[#E5EDFF] text-[#0033CC] px-2 py-0.5">Trigger: {sys.trigger}</span><span className="text-xs font-['JetBrains_Mono'] bg-emerald-50 text-emerald-600 px-2 py-0.5">Saves: {sys.estimated_time_saved}</span></div>
                    {(sys.steps || []).map((s, j) => (<div key={`st-${j}`} className="flex gap-3 py-2 border-b border-slate-50"><span className="text-xs font-['JetBrains_Mono'] text-[#0033CC] font-bold w-6">{String(s.step).padStart(2,"0")}</span><div><p className="text-sm font-medium text-[#0F172A]">{s.action}</p><p className="text-xs text-slate-500">{s.details}</p></div></div>))}
                    {sys.sample_messages?.outreach_email && (<div className="mt-3 bg-slate-50 border border-slate-100 p-4"><p className="text-xs font-['JetBrains_Mono'] uppercase text-[#0033CC] mb-2">Sample Outreach</p><p className="text-xs text-slate-600 whitespace-pre-line">{sys.sample_messages.outreach_email}</p></div>)}
                  </div>
                ))}
              </div></TabsContent>
            )}

            {/* AGENT ARCHITECTURE (White Glove) */}
            {report?.agent_architecture?.agents && (
              <TabsContent value="agents"><div className="space-y-4">
                {report.agent_architecture.agents.map((ag, i) => (
                  <div key={`ag-${i}`} className="bg-white border border-slate-200 p-6">
                    <h4 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-1">{ag.name}</h4>
                    <p className="text-sm text-slate-600 mb-3">{ag.role}</p>
                    <div className="flex flex-wrap gap-1 mb-2">{(ag.actions||[]).map((a,j) => <span key={`a-${j}`} className="text-xs bg-[#E5EDFF] text-[#0033CC] px-2 py-0.5">{a}</span>)}</div>
                    <p className="text-xs text-slate-500 italic mt-2">{ag.example_scenario}</p>
                  </div>
                ))}
                {report.agent_architecture.data_flow && <div className="bg-slate-50 border border-slate-200 p-4"><p className="text-xs font-['JetBrains_Mono'] uppercase text-[#0033CC] mb-1">Data Flow</p><p className="text-sm text-slate-600">{report.agent_architecture.data_flow}</p></div>}
              </div></TabsContent>
            )}

            {/* TOOL STACK (White Glove) */}
            {report?.tool_stack?.layers && (
              <TabsContent value="toolstack"><div className="space-y-4">
                {report.tool_stack.layers.map((ly, i) => (
                  <div key={`ly-${i}`} className="bg-white border border-slate-200 p-5">
                    <div className="flex justify-between mb-2"><h4 className="text-sm font-['Outfit'] font-medium">{ly.layer}</h4><span className={`text-xs font-['JetBrains_Mono'] ${ly.priority?.includes("Must") ? "text-emerald-600" : "text-amber-600"}`}>{ly.priority}</span></div>
                    <p className="text-xs text-slate-500 mb-2">{ly.purpose}</p>
                    <p className="text-sm font-['JetBrains_Mono'] text-[#0033CC]">{ly.monthly_cost_range}</p>
                  </div>
                ))}
                <div className="bg-[#0F172A] p-5"><p className="text-xs font-['JetBrains_Mono'] uppercase text-[#818CF8] mb-1">Total Monthly Estimate</p><p className="text-xl font-['Outfit'] text-white">{report.tool_stack.total_monthly_estimate}</p></div>
              </div></TabsContent>
            )}

          </Tabs>
        )}

        {/* Book Call CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0F172A] p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div><h3 className="text-xl font-['Outfit'] font-medium text-white mb-2">Ready to implement?</h3><p className="text-sm text-slate-400 font-['IBM_Plex_Sans']">Book a strategy call to turn these insights into action.</p></div>
          <Button onClick={() => navigate(`/booking/${auditId}`)} className="bg-white text-[#0F172A] hover:bg-slate-100 rounded-none px-8 h-11 font-['Outfit'] font-medium whitespace-nowrap"><Calendar className="w-4 h-4 mr-2" />Book Strategy Call</Button>
        </motion.div>
      </div>
    </div>
  );
}
